import React, { useEffect, useState } from 'react';

interface ScrollbarsProps {
  width: number;
  height: number;
  contentWidth: number;
  contentHeight: number;
  scrollX: number;
  scrollY: number;
  scale: number;
  onScroll: (x: number, y: number) => void;
}

export const Scrollbars: React.FC<ScrollbarsProps> = ({
  width,
  height,
  contentWidth,
  contentHeight,
  scrollX,
  scrollY,
  scale,
  onScroll
}) => {
  const [isDraggingX, setIsDraggingX] = useState(false);
  const [isDraggingY, setIsDraggingY] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [startScrollX, setStartScrollX] = useState(0);
  const [startScrollY, setStartScrollY] = useState(0);

  const scrollBarSize = 12;
  const minThumbSize = 40;

  // Calculate scaled content dimensions
  const scaledContentWidth = contentWidth * scale;
  const scaledContentHeight = contentHeight * scale;

  // Calculate total scrollable area including panned space
  const leftOverflow = Math.max(0, -scrollX);
  const rightOverflow = Math.max(0, scaledContentWidth + scrollX - width);
  const totalScrollableWidth =
    scaledContentWidth + leftOverflow + rightOverflow;

  const topOverflow = Math.max(0, -scrollY);
  const bottomOverflow = Math.max(0, scaledContentHeight + scrollY - height);
  const totalScrollableHeight =
    scaledContentHeight + topOverflow + bottomOverflow;

  // Determine if scrollbars should be shown
  const showHorizontal = totalScrollableWidth > width;
  const showVertical = totalScrollableHeight > height;

  // Calculate available track space
  const horizontalTrackSpace = width - (showVertical ? scrollBarSize : 0);
  const verticalTrackSpace = height - (showHorizontal ? scrollBarSize : 0);

  // Calculate viewport position relative to total scrollable area
  const viewportLeft = leftOverflow;
  const viewportTop = topOverflow;

  // Calculate total scrollable range
  const horizontalScrollRange = totalScrollableWidth - width;
  const verticalScrollRange = totalScrollableHeight - height;

  // Calculate thumb sizes based on viewport/content ratio
  const horizontalThumbSize = Math.max(
    Math.min(
      (width / totalScrollableWidth) * horizontalTrackSpace,
      horizontalTrackSpace
    ),
    minThumbSize
  );
  const verticalThumbSize = Math.max(
    Math.min(
      (height / totalScrollableHeight) * verticalTrackSpace,
      verticalTrackSpace
    ),
    minThumbSize
  );

  // Calculate scroll ratios
  const horizontalScrollRatio = viewportLeft / horizontalScrollRange;
  const verticalScrollRatio = viewportTop / verticalScrollRange;

  // Calculate thumb positions
  const horizontalThumbPosition = Math.max(
    0,
    Math.min(
      horizontalScrollRatio * (horizontalTrackSpace - horizontalThumbSize),
      horizontalTrackSpace - horizontalThumbSize
    )
  );

  const verticalThumbPosition = Math.max(
    0,
    Math.min(
      verticalScrollRatio * (verticalTrackSpace - verticalThumbSize),
      verticalTrackSpace - verticalThumbSize
    )
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingX) {
        const deltaX = e.clientX - startX;
        const scrollableWidth = horizontalTrackSpace - horizontalThumbSize;
        const scrollRatio = deltaX / scrollableWidth;
        const newScrollX = startScrollX - scrollRatio * horizontalScrollRange; // Invert the delta

        // Clamp the scroll value
        const clampedScrollX = Math.max(
          -horizontalScrollRange,
          Math.min(0, newScrollX)
        );

        onScroll(clampedScrollX, scrollY);
      }

      if (isDraggingY) {
        const deltaY = e.clientY - startY;
        const scrollableHeight = verticalTrackSpace - verticalThumbSize;
        const scrollRatio = deltaY / scrollableHeight;
        const newScrollY = startScrollY - scrollRatio * verticalScrollRange; // Invert the delta

        // Clamp the scroll value
        const clampedScrollY = Math.max(
          -verticalScrollRange,
          Math.min(0, newScrollY)
        );

        onScroll(scrollX, clampedScrollY);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingX(false);
      setIsDraggingY(false);
    };

    if (isDraggingX || isDraggingY) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isDraggingX,
    isDraggingY,
    startX,
    startY,
    startScrollX,
    startScrollY,
    horizontalScrollRange,
    verticalScrollRange,
    horizontalTrackSpace,
    verticalTrackSpace,
    horizontalThumbSize,
    verticalThumbSize,
    scrollX,
    scrollY,
    onScroll
  ]);

  const handleHorizontalMouseDown = (e: React.MouseEvent) => {
    setIsDraggingX(true);
    setStartX(e.clientX);
    setStartScrollX(scrollX);
  };

  const handleVerticalMouseDown = (e: React.MouseEvent) => {
    setIsDraggingY(true);
    setStartY(e.clientY);
    setStartScrollY(scrollY);
  };

  if (!showHorizontal && !showVertical) return null;

  return (
    <>
      {showHorizontal && (
        <div
          className="absolute bottom-0 left-0 bg-gray-100 hover:bg-gray-200 transition-colors"
          style={{
            height: scrollBarSize,
            width: horizontalTrackSpace
          }}
        >
          <div
            className="absolute bg-gray-400 hover:bg-gray-500 rounded cursor-pointer transition-colors"
            style={{
              width: horizontalThumbSize,
              height: scrollBarSize - 4,
              left: horizontalThumbPosition,
              top: 2
            }}
            onMouseDown={handleHorizontalMouseDown}
          />
        </div>
      )}
      {showVertical && (
        <div
          className="absolute top-0 right-0 bg-gray-100 hover:bg-gray-200 transition-colors"
          style={{
            width: scrollBarSize,
            height: verticalTrackSpace
          }}
        >
          <div
            className="absolute bg-gray-400 hover:bg-gray-500 rounded cursor-pointer transition-colors"
            style={{
              height: verticalThumbSize,
              width: scrollBarSize - 4,
              top: verticalThumbPosition,
              left: 2
            }}
            onMouseDown={handleVerticalMouseDown}
          />
        </div>
      )}
    </>
  );
};
